import { getApiBaseUrl } from '../config/apiConfig';
import type { ApiConfig } from '../types/api';
import { isDevelopment } from '../utils/environment';
import { BaseApiClient } from './apiClient';

export interface PhoneNumber {
  countryCode: string;
  tollFree: boolean;
  formattedNumber: string;
}

export interface ConfMapperResponse {
  conference: string;
  fqn: string;
  hasPasscode: boolean | null;
  id: string;
  message: string;
  name: string;
  sipAudioOnlyUri: string | null;
  sipUri: string | null;
  tenant: string;
  url: string;
}

/**
 * Service for phone-related API requests (e.g. conf-mapper, dial-in)
 */
export class PhoneService extends BaseApiClient {
  constructor(config?: Partial<ApiConfig>) {
    const isDevMode = isDevelopment();

    super({
      baseUrl: getApiBaseUrl(isDevMode),
      ...config,
    });
  }

  /**
   * Fetch conference mapper data for a room (PIN, dial-in URL, etc.)
   */
  async getConfMapper(room: string): Promise<ConfMapperResponse> {
    const response = await this.post<ConfMapperResponse>('/phones/app/conf-mapper', { room });
    return response.data;
  }

  /**
   * Fetch phone list (dial-in numbers) for a room
   */
  async getPhoneList(room: string): Promise<PhoneNumber[]> {
    const response = await this.get<PhoneNumber[]>('/phones/app/phone-list', { room });
    return response.data;
  }

  /**
   * Fetch phone list without auth (for the public dial-in page)
   */
  async getPhoneListPublic(): Promise<PhoneNumber[]> {
    const response = await this.getWithoutAuth<PhoneNumber[]>('/phones/app/phone-list');
    return response.data;
  }

  /**
   * Fetch conference mapper data without auth (for the public dial-in page)
   */
  async getConfMapperPublic(room: string): Promise<ConfMapperResponse> {
    const response = await this.postWithoutAuth<ConfMapperResponse>('/phones/app/conf-mapper', {
      room,
    });
    return response.data;
  }
}

// Create singleton instance
export const phoneService = new PhoneService();
